import React, { useEffect, useMemo, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Card, CardContent, Divider, FormControlLabel, Grid, Radio, RadioGroup, TextField, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useLocation } from 'react-router-dom';

const YEARS = 5;
const DEFAULT_WORK_DAYS_PER_WEEK = 5;
const DEFAULT_WORK_WEEKS_PER_YEAR = 48;
const DEFAULT_SCANNERS = 1;
const DEFAULT_TECH_FEE_PER_CASE = 55;
const DEFAULT_PROFESSIONAL_FEE_PER_CASE = 48;
const DEFAULT_ONE_TIME_COST = 20000;
const DEFAULT_PRICE_PER_UNIT = 10000; // $10k per scanner or per FTSE
const FTSE_DIVISOR = 4320; // 5 days/week * 48 weeks/year * 18 cases/day

function clampNumber(value, { min = 0, max = Number.POSITIVE_INFINITY } = {}) {
  if (Number.isNaN(value)) return min;
  return Math.min(max, Math.max(min, value));
}

function getFirstNumberParam(params, keys) {
  for (const key of keys) {
    if (!params.has(key)) continue;
    const raw = params.get(key);
    const parsed = Number(raw);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function formatCurrencyCAD(value) {
  const amount = Number.isFinite(value) ? value : 0;
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatNumber(value) {
  const n = Number.isFinite(value) ? value : 0;
  return new Intl.NumberFormat('en-CA', { maximumFractionDigits: 0 }).format(n);
}

function formatPercent(value) {
  if (!Number.isFinite(value)) return '—';
  return new Intl.NumberFormat('en-CA', {
    style: 'percent',
    maximumFractionDigits: 0,
  }).format(value);
}

function formatNumberWithCommas(value) {
  const n = Number.isFinite(value) ? value : 0;
  return new Intl.NumberFormat('en-CA', { maximumFractionDigits: 0 }).format(n);
}

function parseNumberWithCommas(value) {
  return value.replace(/,/g, '');
}

const ROICalculator = ({ isMobile }) => {
  const location = useLocation();

  const [additionalCasesPerDayRaw, setAdditionalCasesPerDayRaw] = useState('2');
  const [scannersRaw, setScannersRaw] = useState(String(DEFAULT_SCANNERS));
  const [workDaysPerWeekRaw, setWorkDaysPerWeekRaw] = useState(String(DEFAULT_WORK_DAYS_PER_WEEK));
  const [workWeeksPerYearRaw, setWorkWeeksPerYearRaw] = useState(String(DEFAULT_WORK_WEEKS_PER_YEAR));
  const [techFeePerCaseRaw, setTechFeePerCaseRaw] = useState(String(DEFAULT_TECH_FEE_PER_CASE));
  const [professionalFeePerCaseRaw, setProfessionalFeePerCaseRaw] = useState(String(DEFAULT_PROFESSIONAL_FEE_PER_CASE));
  const [oneTimeCostRaw, setOneTimeCostRaw] = useState(String(DEFAULT_ONE_TIME_COST));
  const [feeType, setFeeType] = useState('both'); // 'techOnly' or 'both'
  const [pricingModel, setPricingModel] = useState('perScanner'); // 'perScanner' or 'perFTSE'
  const [pricePerUnitRaw, setPricePerUnitRaw] = useState(String(DEFAULT_PRICE_PER_UNIT));
  const [annualCasesRaw, setAnnualCasesRaw] = useState('');

  // Allow pre-filling key fields via URL params for easy sharing.
  // Examples:
  // /roi-calculator?additionalCasesPerDay=3&scanners=2&annualCost=12000&oneTimeCost=5000&techFeePerCase=55&professionalFeePerCase=48&feeType=techOnly
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    const pAdditionalCasesPerDay = getFirstNumberParam(params, [
      'additionalCasesPerDay',
      'casesPerDay',
      'cases',
    ]);
    if (pAdditionalCasesPerDay !== null) setAdditionalCasesPerDayRaw(String(pAdditionalCasesPerDay));

    const pScanners = getFirstNumberParam(params, ['scanners', 'numScanners', 'nScanners']);
    if (pScanners !== null) setScannersRaw(String(pScanners));

    const pTechFeePerCase = getFirstNumberParam(params, ['techFeePerCase', 'techFee', 'tFee', 'tech_fee']);
    if (pTechFeePerCase !== null) setTechFeePerCaseRaw(String(pTechFeePerCase));

    const pProfessionalFeePerCase = getFirstNumberParam(params, ['professionalFeePerCase', 'professionalFee', 'pFee', 'profFee', 'professional_fee']);
    if (pProfessionalFeePerCase !== null) setProfessionalFeePerCaseRaw(String(pProfessionalFeePerCase));

    const pOneTimeCost = getFirstNumberParam(params, ['oneTimeCost', 'one_time_cost', 'onetimeCost']);
    if (pOneTimeCost !== null) setOneTimeCostRaw(String(pOneTimeCost));

    const pAnnualCases = getFirstNumberParam(params, ['annualCases', 'annual_cases', 'casesPerYear', 'annualCasesPerYear']);
    if (pAnnualCases !== null) setAnnualCasesRaw(String(pAnnualCases));

    const pPricePerUnit = getFirstNumberParam(params, ['pricePerUnit', 'price_per_unit', 'pricePerScanner', 'pricePerFtse', 'unitPrice']);
    if (pPricePerUnit !== null) setPricePerUnitRaw(String(pPricePerUnit));

    // Handle pricingModel parameter
    const pricingModelParam = params.get('pricingModel') || params.get('pricing_model') || params.get('pricing');
    if (pricingModelParam) {
      const normalized = pricingModelParam.toLowerCase().trim();
      if (normalized === 'perscanner' || normalized === 'scanner' || normalized === 'scanners') {
        setPricingModel('perScanner');
      } else if (normalized === 'perftse' || normalized === 'ftse' || normalized === 'casevolume' || normalized === 'volume') {
        setPricingModel('perFTSE');
      }
    }

    // Handle feeType parameter
    const feeTypeParam = params.get('feeType') || params.get('fees') || params.get('fee');
    if (feeTypeParam) {
      const normalized = feeTypeParam.toLowerCase().trim();
      if (normalized === 'techonly' || normalized === 'tech' || normalized === 't' || normalized === 't-only') {
        setFeeType('techOnly');
      } else if (normalized === 'both' || normalized === 'all' || normalized === 'total') {
        setFeeType('both');
      }
    }
  }, [location.search]);

  const additionalCasesPerDay = useMemo(() => {
    const parsed = Number(additionalCasesPerDayRaw);
    return clampNumber(parsed, { min: 0, max: 500 });
  }, [additionalCasesPerDayRaw]);

  const scanners = useMemo(() => {
    const parsed = Number(scannersRaw);
    return clampNumber(parsed, { min: 1, max: 1000 });
  }, [scannersRaw]);

  const workDaysPerWeek = useMemo(() => {
    const parsed = Number(workDaysPerWeekRaw);
    return clampNumber(parsed, { min: 0, max: 7 });
  }, [workDaysPerWeekRaw]);

  const workWeeksPerYear = useMemo(() => {
    const parsed = Number(workWeeksPerYearRaw);
    return clampNumber(parsed, { min: 0, max: 60 });
  }, [workWeeksPerYearRaw]);

  const techFeePerCase = useMemo(() => {
    const parsed = Number(techFeePerCaseRaw);
    return clampNumber(parsed, { min: 0, max: 10000 });
  }, [techFeePerCaseRaw]);

  const professionalFeePerCase = useMemo(() => {
    const parsed = Number(professionalFeePerCaseRaw);
    return clampNumber(parsed, { min: 0, max: 10000 });
  }, [professionalFeePerCaseRaw]);

  const oneTimeCost = useMemo(() => {
    const parsed = Number(oneTimeCostRaw);
    return clampNumber(parsed, { min: 0, max: 10000000 });
  }, [oneTimeCostRaw]);

  const annualCases = useMemo(() => {
    const parsed = Number(annualCasesRaw);
    return clampNumber(parsed, { min: 0, max: 10000000 });
  }, [annualCasesRaw]);

  const pricePerUnit = useMemo(() => {
    const parsed = Number(pricePerUnitRaw);
    return clampNumber(parsed, { min: 0, max: 10000000 });
  }, [pricePerUnitRaw]);

  const ftseEquivalents = useMemo(() => {
    if (annualCases === 0) return 0;
    return annualCases / FTSE_DIVISOR;
  }, [annualCases]);

  // Calculate annual cost based on pricing model
  const annualCost = useMemo(() => {
    if (pricingModel === 'perScanner') {
      return scanners * pricePerUnit;
    } else {
      // perFTSE: case volume -> FTSE equivalents -> x price
      return ftseEquivalents * pricePerUnit;
    }
  }, [pricingModel, scanners, ftseEquivalents, pricePerUnit]);

  const results = useMemo(() => {
    const casesPerYear = additionalCasesPerDay * scanners * workDaysPerWeek * workWeeksPerYear;

    const annualTechFees = casesPerYear * techFeePerCase;
    const annualProfessionalFees = feeType === 'both' ? casesPerYear * professionalFeePerCase : 0;
    const annualTotalFees = annualTechFees + annualProfessionalFees;

    const fiveYearTechFees = annualTechFees * YEARS;
    const fiveYearProfessionalFees = annualProfessionalFees * YEARS;
    const fiveYearTotalFees = annualTotalFees * YEARS;

    const fiveYearCost = oneTimeCost + annualCost * YEARS;
    const fiveYearNetGain = fiveYearTotalFees - fiveYearCost;
    const roi = fiveYearCost > 0 ? fiveYearNetGain / fiveYearCost : Number.NaN;

    return {
      casesPerYear,
      annualTechFees,
      annualProfessionalFees,
      annualTotalFees,
      fiveYearTechFees,
      fiveYearProfessionalFees,
      fiveYearTotalFees,
      fiveYearCost,
      fiveYearNetGain,
      roi,
      ftseEquivalents,
      annualCost,
    };
  }, [additionalCasesPerDay, annualCost, feeType, oneTimeCost, professionalFeePerCase, scanners, techFeePerCase, workDaysPerWeek, workWeeksPerYear, ftseEquivalents]);

  return (
    <Box
      sx={{
        padding: isMobile ? '20px 10px' : '40px',
        backgroundColor: '#f5f7fa',
        minHeight: '100vh',
        transition: 'background-color 0.3s ease',
      }}
    >
      <Box sx={{ maxWidth: '1100px', margin: '0 auto' }}>
        <Typography
          variant="h3"
          sx={{
            color: '#0A3D62',
            textAlign: 'center',
            marginBottom: '10px',
            fontWeight: 'bold',
            fontSize: { xs: '2rem', md: '2.5rem' },
            transition: 'all 0.3s ease',
          }}
        >
          ROI Calculator
        </Typography>
        <Typography
          variant="body1"
          sx={{
            textAlign: 'center',
            marginBottom: '32px',
            color: '#666',
            fontSize: '1.05rem',
            transition: 'color 0.3s ease',
          }}
        >
          Calculate the 5-year ROI from scanning additional cases.
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card 
              sx={{ 
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                borderRadius: '12px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                  transform: 'translateY(-2px)',
                }
              }}
            >
              <CardContent sx={{ padding: '24px' }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: '#0A3D62', 
                    fontWeight: 700, 
                    marginBottom: '24px',
                    fontSize: '1.25rem',
                  }}
                >
                  Inputs & assumptions
                </Typography>

                {/* Workload Section - Expanded by default */}
                <Accordion 
                  defaultExpanded 
                  sx={{ 
                    boxShadow: 'none', 
                    marginBottom: '16px', 
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon sx={{ color: '#666' }} />} 
                    sx={{ 
                      padding: '0',
                      minHeight: '48px',
                      '& .MuiAccordionSummary-content': {
                        margin: '12px 0',
                      }
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ color: '#0A3D62', fontWeight: 600 }}>
                      Workload
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: '0 0 8px 0' }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          label="Additional cases scanned per day"
                          value={additionalCasesPerDayRaw}
                          onChange={(e) => setAdditionalCasesPerDayRaw(e.target.value)}
                          inputMode="decimal"
                          fullWidth
                          helperText="Per scanner, per day"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Number of scanners"
                          value={scannersRaw}
                          onChange={(e) => setScannersRaw(e.target.value)}
                          inputMode="decimal"
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>

                {/* Pricing Model Section - Expanded by default */}
                <Accordion 
                  defaultExpanded 
                  sx={{ 
                    boxShadow: 'none', 
                    marginBottom: '16px', 
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon sx={{ color: '#666' }} />} 
                    sx={{ 
                      padding: '0',
                      minHeight: '48px',
                      '& .MuiAccordionSummary-content': {
                        margin: '12px 0',
                      }
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ color: '#0A3D62', fontWeight: 600 }}>
                      Pricing Model
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: '0 0 8px 0' }}>
                    <Box sx={{ marginBottom: '16px' }}>
                      <RadioGroup
                        value={pricingModel}
                        onChange={(e) => setPricingModel(e.target.value)}
                      >
                        <FormControlLabel 
                          value="perScanner" 
                          control={<Radio />} 
                          label="Per Scanner" 
                          sx={{ marginBottom: '4px' }}
                        />
                        <FormControlLabel 
                          value="perFTSE" 
                          control={<Radio />} 
                          label="Per FTSE (case volume based)" 
                        />
                      </RadioGroup>
                    </Box>
                    <Grid container spacing={2}>
                      {pricingModel === 'perFTSE' && (
                        <Grid item xs={12}>
                          <TextField
                            label="Annual case volume"
                            value={annualCasesRaw === '' ? '' : formatNumberWithCommas(annualCases)}
                            onChange={(e) => {
                              const cleaned = parseNumberWithCommas(e.target.value);
                              setAnnualCasesRaw(cleaned);
                            }}
                            inputMode="numeric"
                            fullWidth
                            helperText={`FTSE equivalents: ${ftseEquivalents.toFixed(2)} (${formatNumber(annualCases)} ÷ ${formatNumber(FTSE_DIVISOR)})`}
                          />
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <TextField
                          label={pricingModel === 'perScanner' ? 'Price per scanner (CAD)' : 'Price per FTSE (CAD)'}
                          value={pricePerUnitRaw === '' ? '' : formatNumberWithCommas(pricePerUnit)}
                          onChange={(e) => {
                            const cleaned = parseNumberWithCommas(e.target.value);
                            setPricePerUnitRaw(cleaned);
                          }}
                          inputMode="numeric"
                          fullWidth
                          helperText={
                            pricingModel === 'perScanner'
                              ? `Annual cost: ${formatCurrencyCAD(annualCost)} (${scanners} scanner${scanners !== 1 ? 's' : ''} × ${formatCurrencyCAD(pricePerUnit)})`
                              : `Annual cost: ${formatCurrencyCAD(annualCost)} (${ftseEquivalents.toFixed(2)} FTSE × ${formatCurrencyCAD(pricePerUnit)})`
                          }
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="One-time cost (CAD)"
                          value={oneTimeCostRaw === '' ? '' : formatNumberWithCommas(oneTimeCost)}
                          onChange={(e) => {
                            const cleaned = parseNumberWithCommas(e.target.value);
                            setOneTimeCostRaw(cleaned);
                          }}
                          inputMode="numeric"
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>

                {/* Work Schedule Section - Collapsed by default */}
                <Accordion 
                  defaultExpanded={false} 
                  sx={{ 
                    boxShadow: 'none', 
                    marginBottom: '16px', 
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon sx={{ color: '#666' }} />} 
                    sx={{ 
                      padding: '0',
                      minHeight: '48px',
                      '& .MuiAccordionSummary-content': {
                        margin: '12px 0',
                      }
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ color: '#0A3D62', fontWeight: 600 }}>
                      Work Schedule
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: '0 0 8px 0' }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          label="Working days per week"
                          value={workDaysPerWeekRaw}
                          onChange={(e) => setWorkDaysPerWeekRaw(e.target.value)}
                          inputMode="decimal"
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Working weeks per year"
                          value={workWeeksPerYearRaw}
                          onChange={(e) => setWorkWeeksPerYearRaw(e.target.value)}
                          inputMode="decimal"
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>

                {/* Fee Settings Section - Collapsed by default */}
                <Accordion 
                  defaultExpanded={false} 
                  sx={{ 
                    boxShadow: 'none', 
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon sx={{ color: '#666' }} />} 
                    sx={{ 
                      padding: '0',
                      minHeight: '48px',
                      '& .MuiAccordionSummary-content': {
                        margin: '12px 0',
                      }
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ color: '#0A3D62', fontWeight: 600 }}>
                      Fee Settings
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: '0 0 8px 0' }}>
                    <Box sx={{ marginBottom: '16px' }}>
                      <Typography variant="body2" sx={{ color: '#666', marginBottom: '8px' }}>
                        Fee type
                      </Typography>
                      <RadioGroup
                        row
                        value={feeType}
                        onChange={(e) => setFeeType(e.target.value)}
                      >
                        <FormControlLabel value="techOnly" control={<Radio />} label="T fees only" />
                        <FormControlLabel value="both" control={<Radio />} label="Both P fees and T fees" />
                      </RadioGroup>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          label="Tech fee per case (CAD)"
                          value={techFeePerCaseRaw}
                          onChange={(e) => setTechFeePerCaseRaw(e.target.value)}
                          inputMode="decimal"
                          fullWidth
                        />
                      </Grid>
                      {feeType === 'both' && (
                        <Grid item xs={12}>
                          <TextField
                            label="Professional fee per case (CAD)"
                            value={professionalFeePerCaseRaw}
                            onChange={(e) => setProfessionalFeePerCaseRaw(e.target.value)}
                            inputMode="decimal"
                            fullWidth
                          />
                        </Grid>
                      )}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            <Card 
              sx={{ 
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                borderRadius: '12px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                  transform: 'translateY(-2px)',
                }
              }}
            >
              <CardContent sx={{ padding: '24px' }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: '#0A3D62', 
                    fontWeight: 700, 
                    marginBottom: '20px',
                    fontSize: '1.25rem',
                  }}
                >
                  Results
                </Typography>

                <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                  <Card 
                    variant="outlined" 
                    sx={{ 
                      borderColor: '#E0E0E0',
                      borderRadius: '10px',
                      background: 'linear-gradient(135deg, rgba(10, 61, 98, 0.05) 0%, rgba(10, 61, 98, 0.02) 100%)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: '#0A3D62',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(10, 61, 98, 0.15)',
                      }
                    }}
                  >
                    <CardContent sx={{ padding: '20px' }}>
                      <Typography variant="subtitle2" sx={{ color: '#666', marginBottom: '10px', fontWeight: 500 }}>
                        Increased cases per year
                      </Typography>
                      <Typography 
                        variant="h4" 
                        sx={{ 
                          color: '#0A3D62', 
                          fontWeight: 800,
                          transition: 'all 0.3s ease',
                        }}
                      >
                        {formatNumber(results.casesPerYear)}
                      </Typography>
                    </CardContent>
                  </Card>

                  <Card 
                    variant="outlined" 
                    sx={{ 
                      borderColor: results.roi >= 0 ? '#E8F5E9' : '#FFEBEE',
                      borderRadius: '10px',
                      background: results.roi >= 0 
                        ? 'linear-gradient(135deg, rgba(46, 125, 50, 0.1) 0%, rgba(46, 125, 50, 0.05) 100%)'
                        : 'linear-gradient(135deg, rgba(198, 40, 40, 0.1) 0%, rgba(198, 40, 40, 0.05) 100%)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: results.roi >= 0 ? '#2e7d32' : '#c62828',
                        transform: 'translateY(-2px)',
                        boxShadow: results.roi >= 0 
                          ? '0 4px 12px rgba(46, 125, 50, 0.2)'
                          : '0 4px 12px rgba(198, 40, 40, 0.2)',
                      }
                    }}
                  >
                    <CardContent sx={{ padding: '20px' }}>
                      <Typography variant="subtitle2" sx={{ color: '#666', marginBottom: '10px', fontWeight: 500 }}>
                        5-year ROI
                      </Typography>
                      <Typography 
                        variant="h4" 
                        sx={{ 
                          color: results.roi >= 0 ? '#2e7d32' : '#c62828', 
                          fontWeight: 800,
                          transition: 'all 0.3s ease',
                        }}
                      >
                        {formatPercent(results.roi)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>

                <Divider sx={{ margin: '24px 0', borderColor: '#E0E0E0' }} />

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        fontWeight: 700, 
                        color: '#0A3D62', 
                        marginBottom: '16px',
                        fontSize: '1.1rem',
                      }}
                    >
                      Annual increase in fees
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '12px 16px' }}>
                      <Typography variant="body2" sx={{ color: '#666', transition: 'color 0.2s ease' }}>Tech fees / year</Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 700,
                          color: '#0A3D62',
                          transition: 'all 0.3s ease',
                        }}
                      >
                        {formatCurrencyCAD(results.annualTechFees)}
                      </Typography>

                      {feeType === 'both' && (
                        <>
                          <Typography variant="body2" sx={{ color: '#666', transition: 'color 0.2s ease' }}>Professional fees / year</Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 700,
                              color: '#0A3D62',
                              transition: 'all 0.3s ease',
                            }}
                          >
                            {formatCurrencyCAD(results.annualProfessionalFees)}
                          </Typography>
                        </>
                      )}

                      <Box sx={{ 
                        gridColumn: '1 / -1', 
                        height: '1px', 
                        backgroundColor: '#E0E0E0', 
                        margin: '4px 0' 
                      }} />

                      <Typography variant="body2" sx={{ color: '#666', fontWeight: 600, transition: 'color 0.2s ease' }}>Total fees / year</Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 800,
                          color: '#0A3D62',
                          fontSize: '1.05rem',
                          transition: 'all 0.3s ease',
                        }}
                      >
                        {formatCurrencyCAD(results.annualTotalFees)}
                      </Typography>
                    </Box>
                    <Box sx={{ marginTop: '16px', padding: '12px', backgroundColor: '#F5F7FA', borderRadius: '8px' }}>
                      <Typography variant="body2" sx={{ color: '#666', marginBottom: '8px', fontWeight: 600 }}>
                        Annual Cost ({pricingModel === 'perScanner' ? 'Per Scanner' : 'Per FTSE'})
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '8px 12px' }}>
                        {pricingModel === 'perScanner' ? (
                          <>
                            <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>
                              {scanners} scanner{scanners !== 1 ? 's' : ''} × {formatCurrencyCAD(pricePerUnit)}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#0A3D62', fontWeight: 600, fontSize: '0.875rem' }}>
                              {formatCurrencyCAD(results.annualCost)}
                            </Typography>
                          </>
                        ) : (
                          <>
                            <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>
                              {results.ftseEquivalents.toFixed(2)} FTSE × {formatCurrencyCAD(pricePerUnit)}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#0A3D62', fontWeight: 600, fontSize: '0.875rem' }}>
                              {formatCurrencyCAD(results.annualCost)}
                            </Typography>
                          </>
                        )}
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        fontWeight: 700, 
                        color: '#0A3D62', 
                        marginBottom: '16px',
                        fontSize: '1.1rem',
                      }}
                    >
                      5-year totals
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '12px 16px' }}>
                      <Typography variant="body2" sx={{ color: '#666', transition: 'color 0.2s ease' }}>
                        Annual cost ({pricingModel === 'perScanner' ? `${scanners} scanner${scanners !== 1 ? 's' : ''}` : `${results.ftseEquivalents.toFixed(2)} FTSE`})
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 700,
                          color: '#0A3D62',
                          transition: 'all 0.3s ease',
                        }}
                      >
                        {formatCurrencyCAD(results.annualCost)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#666', transition: 'color 0.2s ease' }}>5-year cost</Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 800,
                          color: '#0A3D62',
                          transition: 'all 0.3s ease',
                        }}
                      >
                        {formatCurrencyCAD(results.fiveYearCost)}
                      </Typography>

                      <Typography variant="body2" sx={{ color: '#666', transition: 'color 0.2s ease' }}>5-year increase in tech fees</Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 700,
                          color: '#0A3D62',
                          transition: 'all 0.3s ease',
                        }}
                      >
                        {formatCurrencyCAD(results.fiveYearTechFees)}
                      </Typography>

                      {feeType === 'both' && (
                        <>
                          <Typography variant="body2" sx={{ color: '#666', transition: 'color 0.2s ease' }}>5-year increase in professional fees</Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 700,
                              color: '#0A3D62',
                              transition: 'all 0.3s ease',
                            }}
                          >
                            {formatCurrencyCAD(results.fiveYearProfessionalFees)}
                          </Typography>
                        </>
                      )}

                      <Box sx={{ 
                        gridColumn: '1 / -1', 
                        height: '1px', 
                        backgroundColor: '#E0E0E0', 
                        margin: '4px 0' 
                      }} />

                      <Typography variant="body2" sx={{ color: '#666', fontWeight: 600, transition: 'color 0.2s ease' }}>5-year increase in total fees</Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 800,
                          color: '#0A3D62',
                          fontSize: '1.05rem',
                          transition: 'all 0.3s ease',
                        }}
                      >
                        {formatCurrencyCAD(results.fiveYearTotalFees)}
                      </Typography>

                      <Box sx={{ 
                        gridColumn: '1 / -1', 
                        height: '2px', 
                        backgroundColor: '#E0E0E0', 
                        margin: '8px 0 4px 0' 
                      }} />

                      <Typography variant="body2" sx={{ color: '#666', fontWeight: 600, transition: 'color 0.2s ease' }}>Net gain (fees − cost)</Typography>
                      <Typography
                        variant="body2"
                        sx={{ 
                          fontWeight: 800, 
                          color: results.fiveYearNetGain >= 0 ? '#2e7d32' : '#c62828',
                          fontSize: '1.1rem',
                          transition: 'all 0.3s ease',
                        }}
                      >
                        {formatCurrencyCAD(results.fiveYearNetGain)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default ROICalculator;

